<script setup>
import { computed } from 'vue';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from '../provider.js';

const { attachments, content } = useMessageContext();
const { t } = useI18n();

const attachment = computed(() => attachments.value[0]);

const lat = computed(() => {
  const value =
    attachment.value?.coordinatesLat ?? attachment.value?.coordinates_lat;
  return Number(value);
});

const long = computed(() => {
  const value =
    attachment.value?.coordinatesLong ?? attachment.value?.coordinates_long;
  return Number(value);
});

const hasValidCoordinates = computed(() => {
  if (!Number.isFinite(lat.value) || !Number.isFinite(long.value)) {
    return false;
  }

  return lat.value !== 0 || long.value !== 0;
});

const locationMeta = computed(() => attachment.value?.meta || {});

const locationName = computed(() => locationMeta.value.name || '');

const locationAddress = computed(() => {
  const metaAddress = locationMeta.value.address;
  if (metaAddress) return metaAddress;

  const fallback =
    attachment.value?.fallbackTitle ?? attachment.value?.fallback_title;
  if (fallback) return fallback;

  return content.value || '';
});

const showLocationName = computed(() => {
  if (!locationName.value) return false;
  return locationName.value !== locationAddress.value;
});

const mapUrl = computed(() => {
  if (!hasValidCoordinates.value) return '';
  return `https://maps.google.com/?q=${lat.value},${long.value}`;
});

const coordinatesLabel = computed(() => {
  if (!hasValidCoordinates.value) return '';
  return `${lat.value.toFixed(5)}, ${long.value.toFixed(5)}`;
});
</script>

<template>
  <BaseBubble class="overflow-hidden px-3 pb-3 pt-0" data-bubble-name="location">
    <div class="flex flex-col min-w-64 max-w-sm -mx-3">
      <a
        v-if="hasValidCoordinates"
        :href="mapUrl"
        rel="noreferrer noopener nofollow"
        target="_blank"
        class="block group"
      >
        <div
          class="relative h-36 w-full overflow-hidden bg-n-slate-3 dark:bg-n-slate-4"
        >
          <svg
            class="absolute inset-0 size-full text-n-slate-5 dark:text-n-slate-6"
            viewBox="0 0 320 144"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
          >
            <rect width="320" height="144" class="fill-n-slate-4 dark:fill-n-slate-5" />
            <path
              d="M0 48h320M0 96h320M80 0v144M160 0v144M240 0v144"
              fill="none"
              stroke="currentColor"
              stroke-width="1"
            />
            <path
              d="M0 0l320 144M320 0L0 144"
              fill="none"
              stroke="currentColor"
              stroke-width="0.75"
              opacity="0.45"
            />
          </svg>
          <div
            class="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <Icon
              icon="i-ph-map-pin-fill"
              class="size-10 text-n-ruby-11 drop-shadow-md group-hover:scale-110 transition-transform"
            />
          </div>
        </div>
      </a>
      <div
        v-else
        class="flex items-center justify-center h-28 bg-n-alpha-3 text-n-slate-11"
      >
        <Icon icon="i-ph-navigation-arrow-fill" class="size-8" />
      </div>
      <div class="px-3 py-2 space-y-1">
        <p
          v-if="showLocationName"
          class="mb-0 text-sm font-medium text-white"
        >
          {{ locationName }}
        </p>
        <p v-if="locationAddress" class="mb-0 text-sm text-white">
          {{ locationAddress }}
        </p>
        <p
          v-if="coordinatesLabel"
          class="mb-0 text-xs text-white/70 font-mono"
        >
          {{ coordinatesLabel }}
        </p>
      </div>
      <div v-if="hasValidCoordinates" class="px-3 pb-3">
        <a
          :href="mapUrl"
          rel="noreferrer noopener nofollow"
          target="_blank"
          class="w-full block bg-n-solid-3 px-4 py-2 rounded-lg text-sm text-center border border-n-container text-n-slate-12 hover:bg-n-brand hover:text-white hover:border-n-brand transition-colors"
        >
          {{ t('COMPONENTS.LOCATION_BUBBLE.SEE_ON_MAP') }}
        </a>
      </div>
    </div>
  </BaseBubble>
</template>
